/**
 * Onglet Roadbook — écran d'ouverture de l'application.
 * EF-NAV-001, EF-NAV-006, EF-NAV-007, EF-RBK-002 à EF-RBK-014.
 *
 * Le titre mis à part, l'écran n'a qu'une seule zone défilante : la synthèse
 * en est le premier élément et sort du champ dès que l'on descend, puis vient
 * la liste. Deux défilements imbriqués rendraient le parcours de la liste
 * impraticable avec des gants.
 */

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { CategoryKey } from '../../core/categories';
import { isOffline } from '../../platform/offline';
import { countDisplayedPois } from '../../roadbook/display';
import { Disclosure } from '../components/Disclosure';
import { EmptyState } from '../components/EmptyState';
import { MarkerList } from '../components/MarkerList';
import { RoadbookList } from '../components/RoadbookList';
import { SummaryBlock } from '../components/SummaryBlock';
import { SessionNotice } from '../components/SessionNotice';
import { formatDateTime } from '../format';
import { ACTIONS, EMPTY_STATES, MARKERS } from '../labels';
import {
  busy,
  displayRows,
  document_,
  isStale,
  listScrollRequest,
  openSheet,
  parameters,
  purgeUndoCount,
  readOnly,
  roadbookReturnPoiId,
  screen,
  setTitle,
  startSearch,
  undoPurge,
} from '../state';

export function RoadbookScreen(): JSX.Element {
  const current = document_.value;
  const [titleDraft, setTitleDraft] = useState<string | null>(null);

  // Demande émise par la feuille « Aller à un point » (EF-RBK-010). Le jeton
  // permet de redemander la même ligne après un défilement manuel.
  const request = listScrollRequest.value;

  const [returnTarget, setReturnTarget] = useState<number | null>(null);
  const [returnToken, setReturnToken] = useState(0);

  // Retour depuis le mode course, ou POI manuel tout juste enregistré : la
  // ligne concernée est ramenée dans la zone visible.
  useEffect(() => {
    const id = roadbookReturnPoiId.peek();
    if (id === null) return;
    const index = displayRows
      .peek()
      .findIndex((row) => row.kind === 'group' && row.members.some((member) => member.poiId === id));
    roadbookReturnPoiId.value = null;
    if (index < 0) return;
    setReturnTarget(index);
    setReturnToken((value) => value + 1);
  }, []);

  if (current === null) {
    return (
      <div class="screen screen--scroll">
        <SessionNotice />
        <EmptyState
          title={EMPTY_STATES.noRoute.title}
          body={EMPTY_STATES.noRoute.body}
          action={
            <div class="actions" style="justify-content:center;">
              <button
                type="button"
                class="primary"
                onClick={() => {
                  screen.value = 'setup';
                }}
              >
                Ouvrir Import et paramètres
              </button>
            </div>
          }
        />
      </div>
    );
  }

  const rows = displayRows.value;
  const hidden = new Set<CategoryKey>(parameters.value.display.hiddenCategories);
  const track = current.route.track;
  const totals = current.results.totals;
  const offline = isOffline();
  const searching = busy.value === 'recherche de POI';

  const hasPois = current.pois.size > 0;
  const searched =
    current.results.searchIntegrity.coveredRanges.length > 0 ||
    current.results.searchIntegrity.missingRanges.length > 0;
  const displayed = countDisplayedPois(rows);

  // Ligne repliée de la synthèse : le nombre de POI affichés et l'arrivée.
  const summaryHint =
    totals === null
      ? `${displayed} POI · non calculé`
      : `${displayed} POI · arrivée ${formatDateTime(totals.finishLocal)}`;

  const header = (
    <>
      <SessionNotice />
      <MarkerList document={current} stale={isStale.value} />

      <Disclosure title="Synthèse" id="roadbook-synthese" hint={summaryHint}>
        <SummaryBlock
          document={current}
          displayedPoiCount={displayed}
          onPurge={() => {
            openSheet('purge');
          }}
        />
      </Disclosure>

      {purgeUndoCount.value > 0 ? (
        <div class="notice">
          <p>{purgeUndoCount.value} POI supprimés.</p>
          <button
            type="button"
            onClick={() => {
              void undoPurge();
            }}
          >
            {ACTIONS.undo}
          </button>
        </div>
      ) : null}

      {!hasPois && !searched ? (
        <EmptyState
          title={EMPTY_STATES.noSearch.title}
          body={EMPTY_STATES.noSearch.body}
          action={
            <div class="actions" style="justify-content:center;">
              <button
                type="button"
                class="primary"
                disabled={offline || searching}
                onClick={() => {
                  void startSearch();
                }}
              >
                {ACTIONS.search}
              </button>
            </div>
          }
        />
      ) : null}

      {!hasPois && searched ? (
        <EmptyState title={EMPTY_STATES.emptySearch.title} body={EMPTY_STATES.emptySearch.body} />
      ) : null}

      {hasPois && displayed === 0 ? (
        <EmptyState
          title={
            parameters.value.display.hiddenCategories.length > 0 ||
            parameters.value.display.pinnedOnly ||
            parameters.value.display.excludedOnly
              ? EMPTY_STATES.allFiltered.title
              : EMPTY_STATES.allExcluded.title
          }
          body={EMPTY_STATES.allFiltered.body}
          action={
            <div class="actions" style="justify-content:center;">
              <button
                type="button"
                onClick={() => {
                  openSheet('filters');
                }}
              >
                Ouvrir les filtres
              </button>
            </div>
          }
        />
      ) : null}

      {totals === null && hasPois ? (
        <p class="notice">{EMPTY_STATES.notComputed.title} — touchez « Calculer ».</p>
      ) : null}

      {readOnly.value ? <p class="notice">{MARKERS.readOnly}</p> : null}
    </>
  );

  return (
    <div class="screen screen--roadbook">
      <div class="roadbook-head">
        <label class="visually-hidden" for="roadbook-title">
          Titre du roadbook
        </label>
        <input
          id="roadbook-title"
          type="text"
          class="roadbook-title"
          value={titleDraft ?? current.title}
          maxLength={100}
          disabled={readOnly.value}
          onInput={(event) => {
            setTitleDraft((event.currentTarget as HTMLInputElement).value);
          }}
          onBlur={() => {
            if (titleDraft !== null) setTitle(titleDraft);
            setTitleDraft(null);
          }}
        />
      </div>

      {/*
        Une seule zone défilante : l'en-tête et les lignes partagent le
        conteneur de la liste virtualisée (EF-NAV-006).
      */}
      <RoadbookList
        rows={rows}
        document={current}
        pois={current.pois}
        hiddenCategories={hidden}
        routeLengthMeters={track.lengthMeters}
        elevationAvailable={track.elevation.available}
        totalGainMeters={track.elevationGainMeters}
        startLocal={totals?.startLocal ?? null}
        highlightedIndex={null}
        emphasizedLine={null}
        scrollToIndex={request?.index ?? returnTarget}
        scrollRequestId={(request?.token ?? 0) + returnToken}
        onSearchZone={
          offline || readOnly.value
            ? null
            : (from, to): void => {
                void startSearch({ fromProgressionMeters: from, toProgressionMeters: to });
              }
        }
        searchZoneDisabledReason={offline ? 'Indisponible hors ligne.' : null}
        raceView={null}
        header={header}
        preserveScroll
        height="100%"
      />
    </div>
  );
}
